/**
 * NIP-05 verification through the shell instead of through the webview.
 *
 * ROADMAP section 4 records the browser limit this removes: a `nostr.json` served
 * without `Access-Control-Allow-Origin` is fetched and then withheld from the page,
 * so the handle stays unverified no matter how correct the domain's answer was. The
 * document is public, the header protects nobody in this direction, and yet a large
 * share of handles cannot be checked in a browser at all.
 *
 * Only the retrieval moves. The address parsing, the round-trip rule, the TTL cache
 * and the request queue stay in `features/profiles/`, so the desktop build and the
 * web build agree on what "verified" means and there is one place where that
 * definition lives.
 */

import {
  browserNip05Transport,
  type Nip05Transport,
  setNip05Transport,
} from "../profiles/nip05Cache";
import { invokeNative, isDesktopShell } from "./runtime";

/**
 * Ask Rust for a domain's document.
 *
 * The domain and name are passed as separate fields and no URL is built here,
 * which is the point: `fetch_nip05_document` constructs the URL itself so that
 * nothing a note can influence reaches the address bar of a process with no CORS
 * and no origin. See `src-tauri/src/nip05.rs`.
 */
export const nativeNip05Transport: Nip05Transport = async (address) => {
  const body = await invokeNative<string>("fetch_nip05_document", {
    domain: address.domain,
    name: address.local,
  });
  return typeof body === "string" ? body : undefined;
};

/**
 * Install the native transport when running in the shell.
 *
 * Called at module scope from the desktop bridge rather than from an effect, and
 * that ordering is the reason this is a function and not inline: a verification
 * result is cached for minutes, so a transport installed after the first screen has
 * already run its checks would leave exactly the handles it exists to fix marked
 * failed until the TTL expired. Module evaluation happens before the first render;
 * an effect does not.
 */
export function installNativeNip05(): void {
  setNip05Transport(isDesktopShell() ? nativeNip05Transport : undefined);
}

/** Restores the browser path. For tests and for symmetry; nothing else calls it. */
export function installBrowserNip05(): void {
  setNip05Transport(browserNip05Transport());
}
